/* eslint-disable max-lines */
import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import type { TenderDocument } from "../../types"
import type { GPTModelItem, GraphFileItem, UserDocument } from "@/api/models"
import type { TFunction } from "i18next"

import { downloadGraphFileApi } from "@/api/api"
import { downloadSetupFilesZip, downloadTenderDocument } from "@/api/tender-api"
import { Check, Download, FileText, Loader2, Play, Trash2, Upload, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { FilePickerDialog } from "@/features/chat/components/file-picker-dialog"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { CasePeopleFields } from "../case-people-fields"
import {
  documentsFinishedIndexing,
  isIndexingPending,
  procurementDocsReadyToStart,
  questionsDeadlineCommit,
} from "./intake-section-utils"
import {
  useDeleteTenderDocument,
  useSetRunOverrideDocuments,
  useUploadTenderDocuments,
} from "../../hooks/use-tender-case"
import { filterAllowedFiles, TENDER_ACCEPT_FILTER } from "../../tender-upload-constants"

const STATUS_CONFIG: Record<TenderDocument["status"], { labelKey: string; className: string }> = {
  uppladdad: {
    labelKey: "tender.documentStatus.uppladdad",
    className: "bg-accent/50 text-accent-foreground border-accent",
  },
  indexeras: {
    labelKey: "tender.documentStatus.indexeras",
    className: "bg-warning/10 text-warning-foreground border-warning",
  },
  indexerad: {
    labelKey: "tender.documentStatus.indexerad",
    className: "bg-success/15 text-success border-success",
  },
  fel: {
    labelKey: "tender.documentStatus.fel",
    className: "bg-destructive/10 text-destructive border-destructive",
  },
}

function getStatusText(
  t: TFunction,
  {
    hasDocuments,
    waitingForIndexing,
    isExtractionRunning,
  }: {
    hasDocuments: boolean
    waitingForIndexing: boolean
    isExtractionRunning: boolean
  }
): string {
  if (isExtractionRunning) return t("tender.intake.runStatus.extracting")
  if (!hasDocuments) return t("tender.intake.runStatus.uploadToStart")
  if (waitingForIndexing) return t("tender.intake.runStatus.waitIndexing")
  return t("tender.intake.runStatus.readyLive")
}

function getDescriptionText(
  t: TFunction,
  {
    isExtractionRunning,
    hasDocuments,
  }: {
    isExtractionRunning: boolean
    hasDocuments: boolean
  }
): string {
  if (isExtractionRunning) return t("tender.intake.runDescription.extractingWait")
  if (hasDocuments) return t("tender.intake.runDescription.readyStart")
  return t("tender.intake.runDescription.uploadMinimum")
}

function formatSize(kb: number): string {
  if (kb >= 1024) return `${(kb / 1024).toFixed(1)} MB`
  return `${kb} KB`
}

type UploadListProps = {
  caseId: string
  documents: TenderDocument[]
  title: string
  description: string
  onOpenFilePicker: () => void
  onRemove: (docId: string) => Promise<void>
  isUploading: boolean
  removingDocId: string | null
  overrideDocumentIds?: string[]
  onToggleOverride?: (docId: string, isOverride: boolean) => void
  onDropFiles?: (files: File[]) => void
  canEdit?: boolean
  onDownloadZip?: () => void
  isDownloadingZip?: boolean
}

function UploadList({
  caseId,
  documents,
  title,
  description,
  onOpenFilePicker,
  onRemove,
  isUploading,
  removingDocId,
  overrideDocumentIds,
  onToggleOverride,
  onDropFiles,
  canEdit = true,
  onDownloadZip,
  isDownloadingZip = false,
}: UploadListProps) {
  const { t, i18n } = useTranslation()
  const finishedIndexing = documentsFinishedIndexing(documents)
  const [isDragOver, setIsDragOver] = useState(false)
  const isProcessing = (doc: TenderDocument) => isIndexingPending(doc.status)

  const showOverrideToggle = !!onToggleOverride

  let uploadSummary: string
  if (documents.length === 0) {
    uploadSummary = t("tender.intake.uploadList.summaryEmpty")
  } else if (finishedIndexing) {
    uploadSummary = t("tender.intake.uploadList.summaryIndexed", { count: documents.length })
  } else {
    uploadSummary = t("tender.intake.uploadList.summaryIndexing", { count: documents.length })
  }

  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold">{title}</h4>
        <p className="text-muted-foreground text-xs">
          {description} {uploadSummary}
        </p>
      </div>

      {documents.length > 0 && (
        <div className="divide-border divide-y overflow-hidden rounded-lg border">
          {documents.map((doc) => {
            const statusConf = STATUS_CONFIG[doc.status]
            const isRemoving = removingDocId === doc.id
            const canRemove =
              canEdit && !isProcessing(doc) && !isUploading && removingDocId === null
            const isOverride = overrideDocumentIds?.includes(doc.id) ?? false

            return (
              <div
                key={doc.id}
                className={cn(
                  "group flex items-center gap-4 px-5 py-4",
                  isRemoving && "opacity-50",
                  isOverride && "bg-warning/10"
                )}
              >
                <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-lg">
                  <FileText className="text-muted-foreground size-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium">{doc.filename}</p>
                    {isOverride && (
                      <Badge
                        className="border-warning bg-warning/20 text-warning-foreground shrink-0 border font-normal"
                        variant="outline"
                      >
                        {t("tender.quickStart.overrideBadge")}
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {doc.pages > 0 ? t("tender.intake.pagesDot", { count: doc.pages }) : ""}
                    {formatSize(doc.sizeKb)} ·{" "}
                    {new Date(doc.uploadedAt).toLocaleDateString(i18n.language)}
                  </p>
                </div>
                <Badge
                  className={cn("shrink-0 border font-normal", statusConf.className)}
                  variant="outline"
                >
                  {t(statusConf.labelKey)}
                </Badge>
                {showOverrideToggle && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="flex items-center gap-1.5 opacity-0 transition-opacity group-hover:opacity-100 data-[override=true]:opacity-100"
                        data-override={isOverride}
                      >
                        <Switch
                          checked={isOverride}
                          className="data-[state=checked]:bg-warning"
                          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                          onCheckedChange={(checked) => onToggleOverride?.(doc.id, checked)}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      {isOverride
                        ? t("tender.intake.overrideTooltipOn")
                        : t("tender.intake.overrideTooltipOff")}
                    </TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="size-7 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                      size="sm"
                      variant="ghost"
                      onClick={() => void downloadTenderDocument(caseId, doc.id, doc.filename)}
                    >
                      <Download className="text-muted-foreground size-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    {t("tender.extraction.downloadDocument")}
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        disabled={!canRemove || isRemoving}
                        size="sm"
                        variant="ghost"
                        className={cn(
                          "size-7 p-0 opacity-0 transition-opacity group-hover:opacity-100",
                          isRemoving && "opacity-100"
                        )}
                        onClick={() => void onRemove(doc.id)}
                      >
                        <Trash2 className="text-muted-foreground size-3.5" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {isProcessing(doc) && (
                    <TooltipContent>{t("tender.intake.cannotRemoveIndexing")}</TooltipContent>
                  )}
                </Tooltip>
              </div>
            )
          })}
        </div>
      )}

      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            disabled={isUploading}
            size="sm"
            variant="outline"
            className={cn(
              "gap-2 transition-colors",
              isDragOver && !isUploading && "border-primary bg-primary/5 text-primary"
            )}
            onClick={onOpenFilePicker}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={(e) => {
              e.preventDefault()
              if (!isUploading) setIsDragOver(true)
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false)
            }}
            onDrop={(e) => {
              e.preventDefault()
              setIsDragOver(false)
              if (isUploading) return
              const { allowed } = filterAllowedFiles(Array.from(e.dataTransfer.files))
              if (allowed.length > 0) onDropFiles?.(allowed)
            }}
          >
            <Upload className="size-3.5" />
            {isUploading ? t("tender.intake.uploading") : t("tender.intake.addDocument")}
          </Button>
          {onDownloadZip && documents.length > 0 && (
            <Button
              className="gap-2"
              disabled={isDownloadingZip}
              size="sm"
              variant="outline"
              onClick={onDownloadZip}
            >
              {isDownloadingZip ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Download className="size-3.5" />
              )}
              {t("tender.extraction.downloadSetupFilesZip")}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

type IntakeSectionProps = {
  caseId: string
  runId: string
  title: string
  isTitleAutoGenerated: boolean
  runNumber: number
  procurementDocuments: TenderDocument[]
  contextDocuments: TenderDocument[]
  overrideDocumentIds?: string[]
  /** When set, shown as Beställare / client ahead of AI-derived value elsewhere. */
  procuringAuthority?: string | null
  /** Preparer-supplied link to the original case files; included in the handoff email. */
  sourceDocumentsUrl?: string | null
  /** Optional "Sista dag för frågor" (YYYY-MM-DD); used by the material and the handoff email. */
  questionsDeadline?: string | null
  /** The intended Ansvarig. Not the owner — ownership still moves at hand-over. */
  responsibleUserOid?: string | null
  responsibleDisplayName?: string
  responsibleEmail?: string
  ownerUserId?: string
  /** Owner/admin. The people pickers write through participant-management
   *  endpoints, so edit rights are not enough. */
  canManagePeople?: boolean
  onTitleChange: (title: string) => void
  onProcuringAuthoritySave: (value: string | null) => void
  onSourceDocumentsUrlSave: (value: string | null) => void
  onQuestionsDeadlineSave?: (value: string | null) => void
  onStartExtraction?: (modelId?: string) => void
  isExtractionRunning?: boolean
  showModelSelector?: boolean
  availableModels?: GPTModelItem[]
  canEdit?: boolean
  autoPipelineStatus?: string | null
  autoPipelineExpectedDocuments?: number | null
}

export function IntakeSection({
  caseId,
  runId,
  title,
  isTitleAutoGenerated,
  runNumber,
  procurementDocuments,
  contextDocuments,
  overrideDocumentIds = [],
  procuringAuthority = null,
  sourceDocumentsUrl = null,
  questionsDeadline = null,
  responsibleUserOid = null,
  responsibleDisplayName = "",
  responsibleEmail = "",
  ownerUserId,
  canManagePeople = false,
  onTitleChange,
  onProcuringAuthoritySave,
  onSourceDocumentsUrlSave,
  onQuestionsDeadlineSave,
  onStartExtraction,
  isExtractionRunning = false,
  showModelSelector = false,
  availableModels = [],
  canEdit = true,
  autoPipelineStatus = null,
  autoPipelineExpectedDocuments = null,
}: IntakeSectionProps) {
  const { t } = useTranslation()
  const uploadDocuments = useUploadTenderDocuments(caseId)
  const deleteDocument = useDeleteTenderDocument(caseId)
  const setOverrideDocuments = useSetRunOverrideDocuments(caseId)

  const [uploadError, setUploadError] = useState<string | null>(null)
  const [pickerDocType, setPickerDocType] = useState<"procurement" | "context" | null>(null)
  const [titleDraft, setTitleDraft] = useState(title)
  const [procuringDraft, setProcuringDraft] = useState(procuringAuthority ?? "")
  const [sourceUrlDraft, setSourceUrlDraft] = useState(sourceDocumentsUrl ?? "")
  const [questionsDraft, setQuestionsDraft] = useState(questionsDeadline ?? "")
  const [selectedModelId, setSelectedModelId] = useState<string | undefined>(undefined)
  const [isDownloadingSetup, setIsDownloadingSetup] = useState(false)

  useEffect(() => {
    setTitleDraft(title)
  }, [title])

  useEffect(() => {
    setProcuringDraft(procuringAuthority ?? "")
  }, [procuringAuthority])

  useEffect(() => {
    setSourceUrlDraft(sourceDocumentsUrl ?? "")
  }, [sourceDocumentsUrl])

  useEffect(() => {
    setQuestionsDraft(questionsDeadline ?? "")
  }, [questionsDeadline])

  const isDirty = titleDraft !== title
  const procuringDirty = procuringDraft.trim() !== (procuringAuthority ?? "").trim()
  const sourceUrlDirty = sourceUrlDraft.trim() !== (sourceDocumentsUrl ?? "").trim()

  const handleSubmitTitle = () => {
    const trimmed = titleDraft.trim()
    if (!trimmed) {
      setTitleDraft(title)
      return
    }
    onTitleChange(trimmed)
  }

  const handleCancelTitle = () => {
    setTitleDraft(title)
  }

  const handleSubmitProcuring = () => {
    const trimmed = procuringDraft.trim()
    onProcuringAuthoritySave(trimmed ? trimmed : null)
  }

  const handleCancelProcuring = () => {
    setProcuringDraft(procuringAuthority ?? "")
  }

  const handleSubmitSourceUrl = () => {
    const trimmed = sourceUrlDraft.trim()
    onSourceDocumentsUrlSave(trimmed ? trimmed : null)
  }

  const handleCancelSourceUrl = () => {
    setSourceUrlDraft(sourceDocumentsUrl ?? "")
  }

  // Saved on blur/Enter, not per change: a date input reports a "complete" date
  // while the year is still being typed (0002, 0020, 0202…), and one PATCH per
  // keystroke could land out of order.
  const handleCommitQuestionsDeadline = (input: HTMLInputElement) => {
    // A partially cleared native date has value="" and badInput=true; only a
    // completely cleared field represents an intentional removal.
    const commit = input.validity.badInput
      ? ({ kind: "revert" } as const)
      : questionsDeadlineCommit(input.value, questionsDeadline)
    if (commit.kind === "save") onQuestionsDeadlineSave?.(commit.value)
    else if (commit.kind === "revert") {
      input.value = questionsDeadline ?? ""
      setQuestionsDraft(questionsDeadline ?? "")
    }
  }

  const totalDocuments = procurementDocuments.length + contextDocuments.length
  const hasDocuments = totalDocuments > 0
  const waitingForIndexing = procurementDocuments.some((d) => isIndexingPending(d.status))
  const canStart =
    canEdit &&
    hasDocuments &&
    procurementDocsReadyToStart(procurementDocuments) &&
    !isExtractionRunning &&
    !!onStartExtraction

  const uploadingDocType = uploadDocuments.isPending ? uploadDocuments.variables.docType : null
  const removingDocId = deleteDocument.isPending ? deleteDocument.variables.docId : null

  const handleUpload = async (docType: "procurement" | "context", files: File[]) => {
    setUploadError(null)
    try {
      await uploadDocuments.mutateAsync({ runId, docType, files })
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : t("tender.intake.uploadFailed"))
    }
  }

  const handleGraphFileSelect = async (item: GraphFileItem) => {
    if (!pickerDocType) return
    const token = SettingsService.getInstance().token
    const blob = await downloadGraphFileApi(item, token)
    const mime = item.mimeType || blob.type || "application/octet-stream"
    const file = new File([blob], item.name, { type: mime })
    await handleUpload(pickerDocType, [file])
  }

  const handleRemove = async (docId: string) => {
    try {
      await deleteDocument.mutateAsync({ docId, runId })
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : t("tender.intake.removeFailed"))
    }
  }

  const handleToggleOverride = (docId: string, isOverride: boolean) => {
    const next = isOverride
      ? [...new Set([...overrideDocumentIds, docId])]
      : overrideDocumentIds.filter((id) => id !== docId)
    setOverrideDocuments.mutate({ runId, overrideDocumentIds: next })
  }

  const handleDocumentsSelected = async (documents: UserDocument[]) => {
    if (!pickerDocType) return
    const files = documents.map((doc) => {
      const extension = doc.contentFormat === "markdown" ? ".md" : ".txt"
      const safeName = (doc.title || "untitled-document").replace(/[/\\?%*:|"<>]/g, "-")
      return new File([doc.content], `${safeName}${extension}`, { type: "text/plain" })
    })
    await handleUpload(pickerDocType, files)
  }

  let intakeSubtitle: string
  if (autoPipelineStatus === "uploading" && autoPipelineExpectedDocuments) {
    intakeSubtitle = t("tender.intake.subtitleUploading", {
      current: totalDocuments,
      total: autoPipelineExpectedDocuments,
    })
  } else if (hasDocuments) {
    intakeSubtitle = t("tender.intake.subtitleWithDocs", { count: totalDocuments })
  } else {
    intakeSubtitle = t("tender.intake.subtitleEmpty")
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-base font-semibold">{t("tender.sections.intake.title")}</h3>
        <p className="text-muted-foreground text-sm">{intakeSubtitle}</p>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium" htmlFor="tender-name-input">
          {t("tender.intake.tenderNameLabel")}
        </label>
        <form
          className="flex items-center gap-1"
          onSubmit={(event) => {
            event.preventDefault()
            handleSubmitTitle()
          }}
        >
          <Input
            className="flex-1"
            disabled={!canEdit}
            id="tender-name-input"
            placeholder={t("tender.intake.namePlaceholder")}
            readOnly={!canEdit}
            value={titleDraft}
            onChange={(event) => setTitleDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault()
                handleCancelTitle()
              }
            }}
          />
          {isDirty && (
            <>
              <Button
                aria-label={t("tender.header.confirmNameAria")}
                className="size-8 shrink-0"
                disabled={titleDraft.trim().length === 0}
                size="icon"
                type="submit"
                variant="ghost"
              >
                <Check className="size-4" />
              </Button>
              <Button
                aria-label={t("tender.header.cancelRenameAria")}
                className="size-8 shrink-0"
                size="icon"
                type="button"
                variant="ghost"
                onClick={handleCancelTitle}
              >
                <X className="size-4" />
              </Button>
            </>
          )}
        </form>
        <p className="text-muted-foreground text-xs">
          {isTitleAutoGenerated
            ? t("tender.intake.autoNameHint")
            : t("tender.intake.manualNameHint")}
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium" htmlFor="tender-procuring-input">
          {t("tender.intake.procuringAuthorityLabel")}
        </label>
        <form
          className="flex items-center gap-1"
          onSubmit={(event) => {
            event.preventDefault()
            handleSubmitProcuring()
          }}
        >
          <Input
            className="flex-1"
            disabled={!canEdit}
            id="tender-procuring-input"
            placeholder={t("tender.intake.procuringAuthorityPlaceholder")}
            readOnly={!canEdit}
            value={procuringDraft}
            onChange={(event) => setProcuringDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault()
                handleCancelProcuring()
              }
            }}
          />
          {procuringDirty && (
            <>
              <Button
                aria-label={t("tender.intake.confirmProcuringAria")}
                className="size-8 shrink-0"
                size="icon"
                type="submit"
                variant="ghost"
              >
                <Check className="size-4" />
              </Button>
              <Button
                aria-label={t("tender.intake.cancelProcuringAria")}
                className="size-8 shrink-0"
                size="icon"
                type="button"
                variant="ghost"
                onClick={handleCancelProcuring}
              >
                <X className="size-4" />
              </Button>
            </>
          )}
        </form>
        <p className="text-muted-foreground text-xs">{t("tender.intake.procuringAuthorityHint")}</p>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium" htmlFor="tender-source-url-input">
          {t("tender.intake.sourceDocumentsUrlLabel")}
        </label>
        <form
          className="flex items-center gap-1"
          onSubmit={(event) => {
            event.preventDefault()
            handleSubmitSourceUrl()
          }}
        >
          <Input
            className="flex-1"
            disabled={!canEdit}
            id="tender-source-url-input"
            placeholder={t("tender.intake.sourceDocumentsUrlPlaceholder")}
            readOnly={!canEdit}
            value={sourceUrlDraft}
            onChange={(event) => setSourceUrlDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault()
                handleCancelSourceUrl()
              }
            }}
          />
          {sourceUrlDirty && (
            <>
              <Button
                aria-label={t("tender.intake.confirmSourceUrlAria")}
                className="size-8 shrink-0"
                size="icon"
                type="submit"
                variant="ghost"
              >
                <Check className="size-4" />
              </Button>
              <Button
                aria-label={t("tender.intake.cancelSourceUrlAria")}
                className="size-8 shrink-0"
                size="icon"
                type="button"
                variant="ghost"
                onClick={handleCancelSourceUrl}
              >
                <X className="size-4" />
              </Button>
            </>
          )}
        </form>
        <p className="text-muted-foreground text-xs">{t("tender.intake.sourceDocumentsUrlHint")}</p>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium" htmlFor="tender-questions-deadline-input">
          {t("tender.intake.questionsDeadlineLabel")}
        </label>
        <Input
          className="w-48"
          disabled={!canEdit || !onQuestionsDeadlineSave}
          id="tender-questions-deadline-input"
          type="date"
          value={questionsDraft}
          onBlur={(event) => handleCommitQuestionsDeadline(event.currentTarget)}
          onChange={(event) => setQuestionsDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              handleCommitQuestionsDeadline(event.currentTarget)
            } else if (event.key === "Escape") {
              event.preventDefault()
              setQuestionsDraft(questionsDeadline ?? "")
            }
          }}
        />
        <p className="text-muted-foreground text-xs">{t("tender.intake.questionsDeadlineHint")}</p>
      </div>

      <CasePeopleFields
        canManage={canManagePeople}
        caseId={caseId}
        ownerUserId={ownerUserId}
        responsibleDisplayName={responsibleDisplayName}
        responsibleEmail={responsibleEmail}
        responsibleUserOid={responsibleUserOid}
      />

      {uploadError && <p className="text-destructive text-sm">{uploadError}</p>}

      <div className="space-y-5">
        <UploadList
          canEdit={canEdit}
          caseId={caseId}
          description={t("tender.intake.procurementDescription")}
          documents={procurementDocuments}
          isDownloadingZip={isDownloadingSetup}
          isUploading={uploadingDocType === "procurement"}
          overrideDocumentIds={overrideDocumentIds}
          removingDocId={removingDocId}
          title={t("tender.intake.procurementTitle")}
          onDropFiles={(files) => void handleUpload("procurement", files)}
          onOpenFilePicker={() => setPickerDocType("procurement")}
          onRemove={handleRemove}
          onToggleOverride={handleToggleOverride}
          onDownloadZip={async () => {
            setIsDownloadingSetup(true)
            try {
              await downloadSetupFilesZip(caseId, runId, title)
            } finally {
              setIsDownloadingSetup(false)
            }
          }}
        />
        {/* Context/CV upload — re-enable with tender.intake.contextUploadTitle / contextUploadDescription
        <UploadList
          description={t("tender.intake.contextUploadDescription")}
          documents={contextDocuments}
          isUploading={uploadingDocType === "context"}
          removingDocId={removingDocId}
          title={t("tender.intake.contextUploadTitle")}
          onOpenFilePicker={() => setPickerDocType("context")}
          onRemove={handleRemove}
        />
        */}
      </div>

      <FilePickerDialog
        accept={TENDER_ACCEPT_FILTER}
        open={pickerDocType !== null}
        source="recent"
        onDocumentsSelected={handleDocumentsSelected}
        onSelect={handleGraphFileSelect}
        onLocalFilesSelected={async (files) => {
          if (pickerDocType) await handleUpload(pickerDocType, files)
        }}
        onOpenChange={(open) => {
          if (!open) setPickerDocType(null)
        }}
      />

      <div className="border-border border-t pt-5">
        <div className="flex items-center justify-between gap-4">
          <p className="text-muted-foreground text-sm">
            {getDescriptionText(t, { isExtractionRunning, hasDocuments })}
          </p>
          <div className="flex shrink-0 items-center gap-2">
            {showModelSelector && availableModels.length > 0 && (
              <Select
                value={selectedModelId ?? ""}
                onValueChange={(v) => setSelectedModelId(v || undefined)}
              >
                <SelectTrigger className="h-9 w-48 text-xs">
                  <SelectValue placeholder={t("tender.intake.defaultModelPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {availableModels.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    className="gap-2"
                    disabled={!canStart}
                    onClick={() => onStartExtraction?.(selectedModelId)}
                  >
                    {isExtractionRunning ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Play className="size-4" />
                    )}
                    {runNumber > 1 ? t("tender.intake.rerun") : t("tender.intake.startRun")}
                  </Button>
                </span>
              </TooltipTrigger>
              {!canStart && (
                <TooltipContent>
                  {getStatusText(t, { hasDocuments, waitingForIndexing, isExtractionRunning })}
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  )
}
